import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  X, Play, Pause, RotateCcw,
  FlipHorizontal2, Maximize, Minimize, Clock, ArrowLeft
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Skeleton } from '@/components/ui/skeleton';
import { supabase } from '@/integrations/supabase/client';
import { countWords, estimateReadingTime, formatTime } from '@/utils/sanitizeContent';

// Font sizes in rem
const FONT_SIZES = {
  small: '1.5rem',
  medium: '2rem',
  large: '2.5rem',
  xlarge: '3.5rem',
} as const;

type FontSize = keyof typeof FONT_SIZES;

// Speed multipliers
const SPEED_MULTIPLIERS: Record<number, number> = {
  1: 0.3, 2: 0.5, 3: 0.7, 4: 0.9, 5: 1.0,
  6: 1.2, 7: 1.5, 8: 1.8, 9: 2.2, 10: 2.8,
};

const STORAGE_KEY = 'prompter_settings';

interface PrompterSettings {
  speed: number;
  fontSize: FontSize;
  isMirrored: boolean;
}

const DEFAULT_SETTINGS: PrompterSettings = {
  speed: 5,
  fontSize: 'large',
  isMirrored: false,
};

const Prompter = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const scriptId = searchParams.get('id');
  const tableParam = searchParams.get('table') as 'content_ideas' | 'user_generated_content' | null;

  const [content, setContent] = useState('');
  const [title, setTitle] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showCountdown, setShowCountdown] = useState(false);
  const [countdown, setCountdown] = useState(3);
  const [showControls, setShowControls] = useState(true);
  const controlsTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Prompter state
  const [isPlaying, setIsPlaying] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [progress, setProgress] = useState(0);
  const [settings, setSettings] = useState<PrompterSettings>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? { ...DEFAULT_SETTINGS, ...JSON.parse(stored) } : DEFAULT_SETTINGS;
    } catch {
      return DEFAULT_SETTINGS;
    }
  });

  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<number | null>(null);
  const lastTimeRef = useRef<number>(0);

  // Save settings to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch (error) {
      console.warn('Could not save prompter settings to localStorage');
    }
  }, [settings]);

  // Extract script from JSON content (handles creator format)
  const extractScriptContent = (rawContent: string): string => {
    if (!rawContent) return '';

    // First, unescape any literal \n sequences to actual newlines
    let unescaped = rawContent
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/\\n\\n/g, '\n\n')
      .replace(/\\n/g, '\n')
      .replace(/\\t/g, '\t')
      .replace(/\\\\/g, '\\');

    // Try to parse as JSON (creator format: {"title":..., "script":...})
    try {
      const trimmed = unescaped.trim();
      if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
        const parsed = JSON.parse(trimmed);
        // Return script field if present
        if (parsed.script && typeof parsed.script === 'string') {
          return parsed.script;
        }
        // Maybe content is nested
        if (parsed.content && typeof parsed.content === 'string') {
          return parsed.content;
        }
      }
    } catch {
      // Not JSON, return unescaped content
    }

    return unescaped;
  };

  // Load script content
  useEffect(() => {
    const loadContent = async () => {
      if (!scriptId || !tableParam) return;

      setIsLoading(true);
      try {
        const { data: userData } = await supabase.auth.getUser();
        if (!userData.user) throw new Error('Not authenticated');

        const { data, error } = await supabase
          .from(tableParam)
          .select('title, content, description, edited_content')
          .eq('id', scriptId)
          .eq('user_id', userData.user.id)
          .single();

        if (error) throw error;

        // Priority: edited_content > content > description
        const rawContent = data.edited_content || data.content || data.description || '';
        // Extract only the script portion from JSON if needed
        const displayContent = extractScriptContent(rawContent);
        setContent(displayContent);
        setTitle(data.title);
      } catch (error) {
        console.error('Error loading script:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadContent();
  }, [scriptId, tableParam]);

  // Animation loop
  useEffect(() => {
    if (!isPlaying || !contentRef.current || !containerRef.current) {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
      return;
    }

    const containerHeight = containerRef.current.clientHeight;
    const contentHeight = contentRef.current.scrollHeight;
    const maxScroll = Math.max(0, contentHeight - containerHeight + 200); // Extra buffer at end

    const animate = (timestamp: number) => {
      if (!lastTimeRef.current) {
        lastTimeRef.current = timestamp;
      }

      const delta = timestamp - lastTimeRef.current;
      lastTimeRef.current = timestamp;

      const speedMultiplier = SPEED_MULTIPLIERS[settings.speed] || 1;
      const pixelsPerMs = 0.05 * speedMultiplier;
      const scrollDelta = delta * pixelsPerMs;

      setScrollPosition(prev => {
        const newPosition = prev + scrollDelta;

        // Update progress
        const newProgress = maxScroll > 0 ? Math.min(100, (newPosition / maxScroll) * 100) : 0;
        setProgress(newProgress);

        // Stop at end
        if (newPosition >= maxScroll) {
          setIsPlaying(false);
          return maxScroll;
        }

        return newPosition;
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    lastTimeRef.current = 0;
    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isPlaying, settings.speed]);

  // Update scroll position on container - use scrollTop for iOS Safari compatibility
  useEffect(() => {
    if (containerRef.current && isPlaying) {
      containerRef.current.scrollTop = scrollPosition;
    }
  }, [scrollPosition, isPlaying]);

  // Track manual scroll position when not playing
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      if (!isPlaying) {
        setScrollPosition(container.scrollTop);
        // Update progress based on scroll position
        const contentHeight = contentRef.current?.scrollHeight || 0;
        const containerHeight = container.clientHeight;
        const maxScroll = Math.max(0, contentHeight - containerHeight + 200);
        const newProgress = maxScroll > 0 ? Math.min(100, (container.scrollTop / maxScroll) * 100) : 0;
        setProgress(newProgress);
      }
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, [isPlaying]);

  // Fullscreen handling
  const toggleFullscreen = async () => {
    if (!document.fullscreenElement) {
      await document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      await document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Auto-hide controls
  const resetControlsTimeout = useCallback(() => {
    setShowControls(true);
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    controlsTimeoutRef.current = setTimeout(() => {
      if (isPlaying) {
        setShowControls(false);
      }
    }, 3000);
  }, [isPlaying]);

  useEffect(() => {
    const handleMouseMove = () => resetControlsTimeout();
    const handleTouch = () => resetControlsTimeout();

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('touchstart', handleTouch);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('touchstart', handleTouch);
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    };
  }, [resetControlsTimeout]);

  // Countdown before start
  const startWithCountdown = () => {
    setShowCountdown(true);
    setCountdown(3);

    const interval = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          setShowCountdown(false);
          // Get current scroll position from container before starting
          const currentScroll = containerRef.current?.scrollTop || 0;
          setScrollPosition(currentScroll);
          setIsPlaying(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handlePlayToggle = () => {
    if (!isPlaying && scrollPosition === 0) {
      startWithCountdown();
    } else {
      setIsPlaying(prev => !prev);
    }
  };

  const reset = useCallback(() => {
    setIsPlaying(false);
    setScrollPosition(0);
    setProgress(0);
    lastTimeRef.current = 0;
  }, []);

  const setSpeed = useCallback((speed: number) => {
    const clampedSpeed = Math.max(1, Math.min(10, speed));
    setSettings(prev => ({ ...prev, speed: clampedSpeed }));
  }, []);

  const setFontSize = useCallback((fontSize: FontSize) => {
    setSettings(prev => ({ ...prev, fontSize }));
  }, []);

  const toggleMirror = useCallback(() => {
    setSettings(prev => ({ ...prev, isMirrored: !prev.isMirrored }));
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }

      switch (e.key) {
        case ' ':
          e.preventDefault();
          setIsPlaying(prev => !prev);
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSpeed(settings.speed + 1);
          break;
        case 'ArrowDown':
          e.preventDefault();
          setSpeed(settings.speed - 1);
          break;
        case 'r':
        case 'R':
          e.preventDefault();
          reset();
          break;
        case 'Escape':
          if (document.fullscreenElement) {
            document.exitFullscreen();
          } else {
            navigate(-1);
          }
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [settings.speed, navigate, reset]);

  const wordCount = countWords(content);
  const speedMultiplier = SPEED_MULTIPLIERS[settings.speed] || 1;
  const readingTime = estimateReadingTime(wordCount, speedMultiplier);
  const remainingTime = Math.max(0, readingTime * (1 - progress / 100));

  const fontSizeLabels: Record<FontSize, string> = {
    small: 'S',
    medium: 'M',
    large: 'L',
    xlarge: 'XL',
  };

  const getSpeedLabel = (speed: number): string => {
    if (speed <= 3) return 'Wolno';
    if (speed <= 7) return 'Średnio';
    return 'Szybko';
  };

  // Empty state - no script selected
  if (!scriptId || !tableParam) {
    return (
      <div className="h-screen bg-neutral-900 flex flex-col items-center justify-center text-white p-6">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-neutral-800 flex items-center justify-center">
            <MonitorPlay className="w-10 h-10 text-neutral-500" />
          </div>
          <h1 className="text-2xl font-semibold mb-3">Nie wybrano scenariusza</h1>
          <p className="text-neutral-400 mb-6">
            Wybierz scenariusz z biblioteki lub edytora, aby otworzyć go w prompterze.
          </p>
          <Button
            onClick={() => navigate('/biblioteka')}
            className="bg-primary hover:bg-primary/90"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Wróć do biblioteki
          </Button>
        </div>
      </div>
    );
  }

  // Empty content state
  if (!isLoading && !content) {
    return (
      <div className="h-screen bg-neutral-900 flex flex-col items-center justify-center text-white p-6">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-neutral-800 flex items-center justify-center">
            <FileText className="w-10 h-10 text-neutral-500" />
          </div>
          <h1 className="text-2xl font-semibold mb-3">Scenariusz jest pusty</h1>
          <p className="text-neutral-400 mb-6">
            Ten scenariusz nie ma jeszcze treści. Przejdź do edytora, aby dodać tekst.
          </p>
          <div className="flex gap-3 justify-center">
            <Button
              variant="outline"
              onClick={() => navigate('/biblioteka')}
              className="border-neutral-700 text-white hover:bg-neutral-800"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Biblioteka
            </Button>
            <Button
              onClick={() => navigate(`/edytor?id=${scriptId}&table=${tableParam}`)}
              className="bg-primary hover:bg-primary/90"
            >
              <FileEdit className="w-4 h-4 mr-2" />
              Edytuj scenariusz
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-neutral-900 flex flex-col overflow-hidden">
      {/* Countdown overlay */}
      {showCountdown && (
        <div className="absolute inset-0 z-50 bg-neutral-900/95 flex items-center justify-center">
          <div className="text-9xl font-bold text-white animate-pulse">
            {countdown}
          </div>
        </div>
      )}

      {/* Exit button - always visible */}
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate('/biblioteka')}
        className={`absolute top-4 left-4 z-40 text-white/70 hover:text-white hover:bg-white/10 transition-opacity duration-300 ${
          showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
      >
        <X className="w-5 h-5 mr-2" />
        Zamknij prompter
      </Button>

      {/* Fullscreen toggle */}
      <Button
        variant="ghost"
        size="icon"
        onClick={toggleFullscreen}
        className={`absolute top-4 right-4 z-40 text-white/70 hover:text-white hover:bg-white/10 transition-opacity duration-300 ${
          showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
      >
        {isFullscreen ? <Minimize className="w-5 h-5" /> : <Maximize className="w-5 h-5" />}
      </Button>

      {/* Progress bar */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-white/10 z-30">
        <div
          className="h-full bg-primary transition-all duration-100"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Progress percentage - hidden on mobile to avoid overlap */}
      <div className={`absolute top-4 left-1/2 -translate-x-1/2 z-40 bg-white/10 rounded-full px-4 py-1 transition-opacity duration-300 hidden sm:block ${
        showControls ? 'opacity-100' : 'opacity-0'
      }`}>
        <span className="text-white/80 text-sm font-medium">{Math.round(progress)}%</span>
      </div>

      {/* Main content area - use overflow-y-auto for iOS Safari compatibility */}
      <div
        ref={containerRef}
        className="flex-1 overflow-y-auto overflow-x-hidden flex items-start justify-center pt-[20vh] sm:pt-[30vh] pb-[20vh] touch-pan-y"
        onClick={resetControlsTimeout}
        onTouchStart={resetControlsTimeout}
        style={{ scrollBehavior: isPlaying ? 'auto' : 'smooth', WebkitOverflowScrolling: 'touch' }}
      >
        {isLoading ? (
          <div className="space-y-4 w-full max-w-4xl px-4 sm:px-8">
            <Skeleton className="h-12 w-3/4 mx-auto bg-white/10" />
            <Skeleton className="h-12 w-full bg-white/10" />
            <Skeleton className="h-12 w-5/6 mx-auto bg-white/10" />
            <Skeleton className="h-12 w-full bg-white/10" />
            <Skeleton className="h-12 w-2/3 mx-auto bg-white/10" />
          </div>
        ) : (
          <div
            ref={contentRef}
            className="w-full max-w-[800px] px-6 sm:px-8 pb-[30vh] text-center"
            style={{
              fontSize: FONT_SIZES[settings.fontSize],
              lineHeight: '1.8',
              transform: settings.isMirrored ? 'scaleX(-1)' : 'none',
            }}
          >
            {/* Title */}
            <h1 className="text-white/50 mb-8 sm:mb-12 text-xl sm:text-2xl" style={{ fontWeight: 400 }}>
              {title}
            </h1>
            
            {/* Script content - clean styling for iOS Safari */}
            <div 
              className="text-white leading-relaxed whitespace-pre-wrap px-2"
              style={{ fontWeight: 400 }}
            >
              {content}
            </div>
            
            {/* End marker */}
            <div className="mt-[30vh] sm:mt-[50vh] text-white/30 text-lg sm:text-xl" style={{ fontWeight: 400 }}>
              — Koniec scenariusza —
            </div>
          </div>
        )}
      </div>

      {/* Controls panel */}
      <div className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t from-neutral-900 via-neutral-900/95 to-transparent pt-16 pb-8 sm:pb-6 px-3 sm:px-6 z-30 transition-opacity duration-300 safe-area-pb ${
        showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'
      }`}>
        <div className="max-w-2xl mx-auto space-y-3 sm:space-y-4">

          {/* Speed control */}
          <div className="flex items-center gap-2 sm:gap-4">
            <span className="text-white/60 text-xs sm:text-sm w-12 sm:w-20">Wolno</span>
            <Slider
              value={[settings.speed]}
              onValueChange={([value]) => setSpeed(value)}
              min={1}
              max={10}
              step={1}
              className="flex-1"
            />
            <span className="text-white/60 text-xs sm:text-sm w-12 sm:w-20 text-right">Szybko</span>
          </div>

          {/* Main controls */}
          <div className="flex items-center justify-center gap-2 sm:gap-4 flex-wrap">
            {/* Font size controls */}
            <div className="flex items-center gap-0.5 sm:gap-1 bg-white/10 rounded-lg p-0.5 sm:p-1">
              {(['medium', 'large', 'xlarge'] as const).map((size) => (
                <button
                  key={size}
                  onClick={() => setFontSize(size)}
                  className={`px-2 sm:px-3 py-1 sm:py-1.5 rounded text-xs sm:text-sm font-medium transition-colors ${
                    settings.fontSize === size
                      ? 'bg-white text-neutral-900'
                      : 'text-white/60 hover:text-white'
                  }`}
                >
                  {fontSizeLabels[size]}
                </button>
              ))}
            </div>

            {/* Reset */}
            <Button
              variant="ghost"
              size="icon"
              onClick={reset}
              className="text-white/60 hover:text-white hover:bg-white/10 w-8 h-8 sm:w-10 sm:h-10"
            >
              <RotateCcw className="w-4 h-4 sm:w-5 sm:h-5" />
            </Button>

            {/* Play/Pause */}
            <Button
              size="lg"
              onClick={handlePlayToggle}
              className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-primary hover:bg-primary/90"
            >
              {isPlaying ? (
                <Pause className="w-6 h-6 sm:w-8 sm:h-8" />
              ) : (
                <Play className="w-6 h-6 sm:w-8 sm:h-8 ml-0.5 sm:ml-1" />
              )}
            </Button>

            {/* Mirror toggle */}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleMirror}
              className={`w-8 h-8 sm:w-10 sm:h-10 ${
                settings.isMirrored
                  ? 'text-primary bg-primary/20'
                  : 'text-white/60 hover:text-white hover:bg-white/10'
              }`}
            >
              <FlipHorizontal2 className="w-4 h-4 sm:w-5 sm:h-5" />
            </Button>

            {/* Speed indicator */}
            <div className="flex items-center gap-1 bg-white/10 rounded-lg px-2 sm:px-3 py-1 sm:py-1.5">
              <span className="text-white/60 text-xs sm:text-sm hidden sm:inline">{getSpeedLabel(settings.speed)}:</span>
              <span className="text-white font-medium text-xs sm:text-sm">{settings.speed}</span>
            </div>
          </div>

          {/* Keyboard shortcuts hint */}
          <div className="text-center text-white/30 text-[10px] sm:text-xs hidden sm:block">
            Spacja = pauza/odtwarzaj • ↑↓ = zmień tempo • R = reset • Esc = wyjdź
          </div>
        </div>
      </div>
    </div>
  );
};

// Import icons used in empty states
import { MonitorPlay, FileText, FileEdit } from 'lucide-react';

export default Prompter;